import React, { useCallback, useState } from "react";
import { useParams } from "react-router-dom"; // Added useNavigate
import { toast } from "react-toastify";
import AddPlaceInfoModal from "../../components/AddPlaceInfoModal/AddPlaceInfoModal";
import CheckInBox from "../../components/CheckInBox/CheckInBox";
import DataSubmissionStatusSummary from "../../components/DataSubmissionStatusSummary/DataSubmissionStatusSummary";
import ErrorDisplay from "../../components/ErrorDisplay/ErrorDisplay";
import { Headings } from "../../components/Headings";
import LoadingMask from "../../components/LoadingMask/LoadingMask";
import PageLayout from "../../components/PageLayout/PageLayout";
import PhoneLayout from "../../components/PhoneLayout/PhoneLayout";
import PlacePagePlaceInfo from "../../components/Place/PlacePagePlaceInfo/PlacePagePlaceInfo";
import { PlaceNotes } from "../../components/PlaceNotes/PlaceNotes";
import { useAppAuthState } from "../../hooks/useAppAuthState"; // Added
import { usePlaceInfo } from "../../hooks/usePlaceInfo";
import { SitePage } from "../../types/SitePage";
import "./PlacePage.scss";

const PlacePage: React.FC = () => {
  const { placeId } = useParams<{ placeId: string }>();
  const { isAuthLoading, isLoggedIn } = useAppAuthState();
  const [showAddInfoModal, setShowAddInfoModal] = useState(false);

  const {
    place,
    loading: placeLoading,
    error: placeError,
  } = usePlaceInfo(placeId || "");

  const handleAddInfo = useCallback(() => {
    if (isLoggedIn) {
      setShowAddInfoModal(true);
    } else {
      toast.error("Please login to add more info");
    }
  }, [isLoggedIn]);

  if (placeLoading || isAuthLoading) {
    return <LoadingMask message="Loading location details..." />;
  }

  let content;
  if (place) {
    content = (
      <>
        <PlacePagePlaceInfo place={place} />
        <CheckInBox placeId={place?.googlePlaceId} />
        <PlaceNotes place={place} />

        <div className="place-page-add-info">
          <Headings.PhoneSubBox>Know this place?</Headings.PhoneSubBox>
          <div className="gapped-buttons">
            <button className="btn btn-primary" onClick={handleAddInfo}>
              Add more info
            </button>
          </div>
          <DataSubmissionStatusSummary placeId={place.googlePlaceId} />
        </div>

        <AddPlaceInfoModal
          isOpen={showAddInfoModal}
          onClose={() => setShowAddInfoModal(false)}
          googlePlaceId={place.googlePlaceId}
        />

        <div className="place-page-footer">
          <div className="disclaimer">
            All of the above information is sourced from our community and
            verified by our team. If you see any information that is incorrect,
            please let us know.
          </div>
        </div>
      </>
    );
  } else {
    content = <div className="alert alert-danger">Place not found.</div>;
  }

  return (
    <PageLayout page={SitePage.Place} title={place?.name || "Place"}>
      <PhoneLayout>
        {placeError && <ErrorDisplay error={placeError} />}
        <div className="place-page-container">{content}</div>
      </PhoneLayout>
    </PageLayout>
  );
};

export default PlacePage;
